import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleTable, Button, Input, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createCouponColumns } from './tableColumns';
import * as promoApi from '../../../api/PromoCodeAdminApi';
import { transformPromoCode } from './utils';
import ROUTE_PATHS from '../../../routes/paths';
import styles from './CouponCodes.module.scss';
const DEFAULT_FILTERS = {
    discount_type: '',
    is_active: '',
    is_single_use_per_customer: '',
    product_requirement: '',
    start_date_from: '',
    start_date_to: '',
    end_date_from: '',
    end_date_to: '',
};
const CouponCodes = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [promoCodes, setPromoCodes] = useState([]);
    const [pendingIsActiveChanges, setPendingIsActiveChanges] = useState({});
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const loadPromoCodes = useCallback(async (nextFilters = DEFAULT_FILTERS, nextSearchTerm = '') => {
        const effectiveFilters = nextFilters;
        const apiFilters = {
            search: nextSearchTerm || undefined,
            discount_type: effectiveFilters.discount_type || undefined,
            product_requirement: effectiveFilters.product_requirement || undefined,
            start_date_from: effectiveFilters.start_date_from || undefined,
            start_date_to: effectiveFilters.start_date_to || undefined,
            end_date_from: effectiveFilters.end_date_from || undefined,
            end_date_to: effectiveFilters.end_date_to || undefined,
        };
        if (effectiveFilters.is_active !== '') {
            apiFilters.is_active = effectiveFilters.is_active === 'true';
        }
        if (effectiveFilters.is_single_use_per_customer !== '') {
            apiFilters.is_single_use_per_customer = effectiveFilters.is_single_use_per_customer === 'true';
        }
        try {
            setLoading(true);
            const response = await promoApi.fetchPromoCodes(apiFilters);
            const data = response.body;
            const promoArray = Array.isArray(data) ? data : data.results || [];
            const transformedData = promoArray
                .map(transformPromoCode)
                .sort((a, b) => {
                const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
                const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
                return bTime - aTime;
            });
            setPromoCodes(transformedData);
            setPendingIsActiveChanges({});
        }
        catch (error) {
            console.error('Error fetching promo codes:', error);
            showToast({ message: 'Failed to load promo codes', variant: 'error', noBorder: true });
        }
        finally {
            setLoading(false);
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadPromoCodes(appliedFilters, searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadPromoCodes, appliedFilters, searchTerm]);
    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };
    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };
    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    const handleAddCoupon = () => {
        navigate(ROUTE_PATHS.adminPromoCodeNew);
    };
    const handleEditCoupon = (promo) => {
        navigate(ROUTE_PATHS.adminPromoCodeDetails.replace(':promoCodeId', promo.id));
    };
    const handleDeleteCoupon = async (promo) => {
        if (!window.confirm(`Are you sure you want to delete promo code "${promo.promo_code}"?`)) {
            return;
        }
        try {
            await promoApi.deletePromoCode(promo.id);
            showToast({
                message: 'Promo code deleted successfully',
                variant: 'success',
                noBorder: true,
            });
            loadPromoCodes(appliedFilters, searchTerm);
        }
        catch (error) {
            console.error('Error deleting promo code:', error);
            showToast({ message: 'Failed to delete promo code', variant: 'error', noBorder: true });
        }
    };
    const handleToggleIsActive = (promo, isActive) => {
        const originalPromo = promoCodes.find((item) => item.id === promo.id);
        if (!originalPromo)
            return;
        setPendingIsActiveChanges((prev) => {
            if (originalPromo.is_active === isActive) {
                const next = { ...prev };
                delete next[promo.id];
                return next;
            }
            return {
                ...prev,
                [promo.id]: isActive,
            };
        });
    };
    const runBulkUpdateIsActive = async (selectedRows, isActive) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        setIsRunningBulkAction(true);
        try {
            await Promise.all(selectedIds.map((id) => promoApi.updatePromoCode(id, { isActive })));
            setPromoCodes((prev) => prev.map((promo) => (selectedIds.includes(promo.id)
                ? { ...promo, is_active: isActive }
                : promo)));
            setPendingIsActiveChanges((prev) => {
                const next = { ...prev };
                selectedIds.forEach((id) => {
                    delete next[id];
                });
                return next;
            });
            showToast({
                message: `Updated ${selectedIds.length} promo code${selectedIds.length === 1 ? '' : 's'}`,
                variant: 'success',
                noBorder: true,
            });
        }
        catch (error) {
            console.error('Error running bulk update:', error);
            showToast({ message: 'Failed to update selected promo codes', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleBulkDeleteSelected = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected promo code${selectedIds.length === 1 ? '' : 's'}?`)) {
            return;
        }
        setIsRunningBulkAction(true);
        try {
            await Promise.all(selectedIds.map((id) => promoApi.deletePromoCode(id)));
            setPromoCodes((prev) => prev.filter((promo) => !selectedIds.includes(promo.id)));
            setPendingIsActiveChanges((prev) => {
                const next = { ...prev };
                selectedIds.forEach((id) => {
                    delete next[id];
                });
                return next;
            });
            showToast({
                message: `Deleted ${selectedIds.length} promo code${selectedIds.length === 1 ? '' : 's'}`,
                variant: 'success',
                noBorder: true,
            });
        }
        catch (error) {
            console.error('Error deleting selected promo codes:', error);
            showToast({ message: 'Failed to delete selected promo codes', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleBulkSave = async () => {
        const changedEntries = Object.entries(pendingIsActiveChanges);
        if (!changedEntries.length) {
            return;
        }
        setIsSaving(true);
        try {
            await Promise.all(changedEntries.map(([id, isActive]) => promoApi.updatePromoCode(id, { isActive })));
            setPromoCodes((prev) => prev.map((promo) => (pendingIsActiveChanges[promo.id] !== undefined
                ? { ...promo, is_active: pendingIsActiveChanges[promo.id] }
                : promo)));
            setPendingIsActiveChanges({});
            showToast({
                message: 'Promo codes updated successfully',
                variant: 'success',
                noBorder: true,
            });
        }
        catch (error) {
            console.error('Error saving promo code updates:', error);
            showToast({ message: 'Failed to save promo code updates', variant: 'error', noBorder: true });
        }
        finally {
            setIsSaving(false);
        }
    };
    const displayPromoCodes = promoCodes.map((promo) => (pendingIsActiveChanges[promo.id] !== undefined
        ? { ...promo, is_active: pendingIsActiveChanges[promo.id] }
        : promo));
    const changedCount = Object.keys(pendingIsActiveChanges).length;
    const columns = createCouponColumns(handleEditCoupon, handleDeleteCoupon, handleToggleIsActive);
    const bulkActions = [
        {
            label: 'Activate',
            action: (selectedRows) => runBulkUpdateIsActive(selectedRows, true),
        },
        {
            label: 'Deactivate',
            action: (selectedRows) => runBulkUpdateIsActive(selectedRows, false),
        },
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDeleteSelected(selectedRows),
        },
    ];
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Discount Type" }), _jsxs("select", { value: filters.discount_type, onChange: (event) => handleFilterChange('discount_type', event.target.value), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "flat", children: "Flat Amount" }), _jsx("option", { value: "percent", children: "Percentage" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Is Active" }), _jsxs("select", { value: filters.is_active, onChange: (event) => handleFilterChange('is_active', event.target.value), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Active" }), _jsx("option", { value: "false", children: "Inactive" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Single Use Per Customer" }), _jsxs("select", { value: filters.is_single_use_per_customer, onChange: (event) => handleFilterChange('is_single_use_per_customer', event.target.value), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Yes" }), _jsx("option", { value: "false", children: "No" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Product Requirement" }), _jsxs("select", { value: filters.product_requirement, onChange: (event) => handleFilterChange('product_requirement', event.target.value), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "any", children: "Any" }), _jsx("option", { value: "all", children: "All" })] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Start Date From" }), _jsx("input", { type: "date", value: filters.start_date_from, onChange: (event) => handleFilterChange('start_date_from', event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "Start Date To" }), _jsx("input", { type: "date", value: filters.start_date_to, onChange: (event) => handleFilterChange('start_date_to', event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "End Date From" }), _jsx("input", { type: "date", value: filters.end_date_from, onChange: (event) => handleFilterChange('end_date_from', event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { children: "End Date To" }), _jsx("input", { type: "date", value: filters.end_date_to, onChange: (event) => handleFilterChange('end_date_to', event.target.value) })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: handleApplyFilters }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleResetFilters })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by promo code or description", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: columns, data: displayPromoCodes, rowHeight: "60px", tableHeading: "Promo Codes", titleStyle: {
                                color: '#000',
                                fontFamily: 'Nunito',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '28px',
                            }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Promo codes per page", paginationItemsLabel: "Promo Codes", onRowClick: (promo) => handleEditCoupon(promo), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Promo Code", onClick: handleAddCoupon }) }), _jsxs("div", { className: styles.bulkActions, children: [_jsxs("span", { className: styles.changeCount, children: [changedCount, " change", changedCount === 1 ? '' : 's', " pending"] }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSaving ? 'Saving...' : 'Save', onClick: handleBulkSave, disabled: isSaving || changedCount === 0 })] })] })] }) }));
};
export default CouponCodes;
